import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Stack } from '@mui/material';
import get from 'lodash-es/get';
import React, { useCallback, useEffect, useRef } from 'react';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_CONTRACT_URL, API_CONTRACTS_URL } from '../../constants/apiUrls';
import { contractFormText } from '../../constants/contracts';
import { EDIT_LINE_ITEM_URL, NEW_LINE_ITEM_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import useModal from '../../hooks/useModal';
import { useSnackBar } from '../../hooks/useSnackBar';
import mapContractToForm from '../../util/mapContractToForm';
import mapFormToContract from '../../util/mapFormToContract';
import { getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';
import ContractSchema, { Contract } from '../../validationSchemas/ContractSchema';
import FormEndOptions from '../forms/FormEndOptions';
import ContractBudgetRatesSection from './ContractBudgetRatesSection';
import ContractClientBillingSection from './ContractClientBillingSection';
import ContractGeneralSection from './ContractGeneralSection';
import ContractKpiSection from './ContractKpiSection';
import ContractReportingSection from './ContractReportingSection';
import DuplicateContractForm from './DuplicateContractForm';

interface ContractFormProps {
  contractData: any;
  existingAdvertiserData: any;
}

function ContractForm({ contractData, existingAdvertiserData }: ContractFormProps) {
  const contractId = contractData?.id;
  const isNewContract = Boolean(contractId) === false;
  const formOpenedAt = useRef(Date.now());
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const contractConfigurationContext = useContractConfigurationContext();

  const lineItemsData = get(contractConfigurationContext, 'lineItemsData');
  const mainBodyWidth = get(contractConfigurationContext, 'mainBodyWidth');

  let nextUrl = generatePath(NEW_LINE_ITEM_URL, { id: contractId?.toString() || '' });
  if (lineItemsData && lineItemsData.length > 0) {
    nextUrl = generatePath(EDIT_LINE_ITEM_URL, {
      id: contractId.toString(),
      lineItemId: lineItemsData[0].id.toString(),
    });
  }

  const defaultValues: Contract = mapContractToForm(
    contractData,
    // TODO: the integration tests create new contracts that have no advertiser data.
    // Therefore, if the contract is not new and has no advertiser data,
    // then just stub it with zero as IDs
    existingAdvertiserData || (!isNewContract && { id: 0, client: { id: 0 } })
  );

  const contractForm = useForm<Contract>({
    resolver: zodResolver(ContractSchema),
    defaultValues,
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
    setValue,
    setError,
    watch,
    reset,
    resetField,
  } = contractForm;

  useEffect(() => {
    if (contractData && contractData.id) {
      reset(mapContractToForm(contractData, existingAdvertiserData));
    }
  }, [contractData, existingAdvertiserData, reset]);

  useConfirmNavChange(() => isDirty);

  const getCategories = useCallback(
    async (clientId) => {
      try {
        const response = await getRequestJSON(
          `${apiHost}/admin/contracts/client_driven_defaults?client_id=${clientId}`
        );
        return response.categorization;
      } catch {
        return null;
      }
    },
    [apiHost]
  );

  const updateCategories = useCallback(
    async (clientId) => {
      let fetchedCategories = [];
      if (clientId) {
        fetchedCategories = await getCategories(clientId);
      }
      resetField('categorization', { defaultValue: fetchedCategories });
      // Put a delay to ensure the form is updated with the new categorization defaults
      setTimeout(() => {
        setValue('categorization', fetchedCategories);
      }, 10);
    },
    [setValue, resetField, getCategories]
  );

  // #region form change side effects
  // update hidden categorization when selected client changes
  const watchedSelectedClientId = watch('selectedClientId');
  useEffect(() => {
    // get default categories
    if (watchedSelectedClientId) {
      updateCategories(watchedSelectedClientId);
    }
  }, [watchedSelectedClientId, updateCategories]);

  // update pacing strategies array on change
  // (API expects pacing strategies as an array of strings)
  const watchedSlowStart = watch('slowStart');
  const watchedFastCatchup = watch('fastCatchup');
  useEffect(() => {
    const setValueOptions = { shouldDirty: false, shouldValidate: false };
    const currentStrategies = getValues('pacingStrategies');
    const memoArray = currentStrategies;

    if (watchedSlowStart) {
      if (!memoArray.includes('slowstart')) {
        memoArray.push('slowstart');
      }
    } else if (memoArray.includes('slowstart')) {
      const index = memoArray.indexOf('slowstart');
      memoArray.splice(index, 1);
    }

    if (watchedFastCatchup) {
      if (!memoArray.includes('fastcatchup')) {
        memoArray.push('fastcatchup');
      }
    } else if (memoArray.includes('fastcatchup')) {
      const index = memoArray.indexOf('fastcatchup');
      memoArray.splice(index, 1);
    }

    setValue('pacingStrategies', memoArray, setValueOptions);
  }, [watchedSlowStart, watchedFastCatchup, getValues, setValue]);
  // #endregion

  // loop over API error strings in order to attribute them to the correct field
  const mapAPIErrorsToFields = (errorArray: string[]) => {
    errorArray.forEach((error) => {
      if (
        error.includes('Contract cpc must be greater than or equal to') ||
        error.includes('Contract cpm must be greater than or equal to')
      ) {
        setError('pricingRate', {
          type: 'manual',
          message: error,
        });
      } else if (/LCI Campaign/i.test(error)) {
        setError('contractLciCampaigns', {
          type: 'manual',
          message: error,
        });
      } else if (/KPI/i.test(error) || error.includes('At least one KPI')) {
        setError('contractKpis', {
          type: 'manual',
          message: error,
        });
      }
    });
    showSnackBar(errorArray.join('. '), 'error', { event: 'contract_save_error', contract_id: contractId });
    return errors;
  };

  const handleSubmitErrors: SubmitErrorHandler<Contract> = () => {
    showSnackBar('There were some errors on the form. Please check all the fields to see if there are any error messages.', 'error', { event: 'contract_save_error', contract_id: contractId });
  };

  const handleFormSubmit = async (data) => {
    const submissionBody = mapFormToContract(data);

    try {
      let response;
      if (isNewContract) {
        response = await postRequestJSON(apiHost, generatePath(API_CONTRACTS_URL), submissionBody);
      } else {
        response = await putRequestJSON(
          apiHost,
          generatePath(API_CONTRACT_URL, { contractId: data.contractId.toString() }),
          submissionBody
        );
      }

      if (response.errors) {
        mapAPIErrorsToFields(response.errors);
      } else if (response.model?.id) {
        showSnackBar('Contract Saved!', 'success', {
          event: isNewContract ? 'contract_created' : 'contract_updated',
          contract_id: response.model.id,
          contract_name: response.model.name,
          formOpenedAt: formOpenedAt.current,
        });

        const updatedFormData = mapContractToForm(response.model, existingAdvertiserData);

        reset(updatedFormData);

        if (isNewContract) {
          setTimeout(() => {
            navigate(generatePath(NEW_LINE_ITEM_URL, { id: response.model.id }));
          }, 1000);
        }
      } else {
        const pageError = 'Something went wrong on this page! Please share this error with #special-ops.';
        showSnackBar(pageError, 'error', { event: 'contract_save_error', contract_id: contractId });
      }
    } catch (error) {
      showSnackBar(
        `Failed to save contract: ${error.message || 'Unknown error'}.`,
        'error',
        { event: 'contract_save_error' }
      );
    }
  };

  const [showDuplicateContractForm, closeDuplicateContractForm] = useModal(
    () => <DuplicateContractForm contract={contractData} onClose={closeDuplicateContractForm} />,
    null,
    [contractData]
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...contractForm}>
      <form onSubmit={handleSubmit(handleFormSubmit, handleSubmitErrors)}>
        <Stack p={2} pt={1} pb={10} spacing={2}>
          <ContractGeneralSection />
          <ContractBudgetRatesSection />
          <ContractClientBillingSection />
          <ContractReportingSection />
          <ContractKpiSection />
        </Stack>
        <FormEndOptions position="fixed" width={`${mainBodyWidth}px`}>
          {isNewContract && (
            <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          )}
          {!isNewContract && (
            <Button type="button" variant="outlined" color="primary" onClick={showDuplicateContractForm}>
              {contractFormText.contractEditDuplicate}
            </Button>
          )}
          <Button type="submit" color="primary" disabled={!isDirty}>
            {isNewContract ? contractFormText.contractNewSave : contractFormText.contractEditSave}
          </Button>
          {/* TODO: finish next-without-save button */}
          {!isNewContract && !isDirty && (
            <Button type="button" variant="contained" color="primary" onClick={() => navigate(nextUrl)}>
              {contractFormText.contractEditNext}
            </Button>
          )}
        </FormEndOptions>
      </form>
    </FormProvider>
  );
}

export default ContractForm;
